/**
 * The coordination plane, from the browser (`hosted.md §6.4`).
 *
 * TWO CREDENTIALS, NOT INTERCHANGEABLE: opening a room and taking a seat need a person and carry the
 * Sanctum bearer; what a seat then does carries only its ROOM TOKEN, in a header of its own, because
 * `Authorization` already means a Sanctum token across that API and two credentials under one name is
 * how a route reads the wrong one.
 *
 * The backend is a different ORIGIN from this page, which is why `backend/config/cors.php` exists.
 */
export type Seat = { peer: string; role: 'host' | 'guest'; token: string; expires_at: string };
/**
 * ONE ROW OF THE ROSTER, AS THE PLANE KNOWS IT (`hosted.md §9`).
 *
 * THE THREE STATES ARE THE POINT AND THEY ARE THE PLANE'S, NOT THE GAME'S. `invited` · `accepted` ·
 * `connected` answer "whom is the host still waiting for", and nothing in the match can answer that: the
 * authority only ever learns about somebody at the instant he opens a channel, so from where it sits an
 * invitation nobody replied to and an invitation that was never sent look identical. (`left` is the
 * fourth and it is an ending, not a wait.)
 *
 * `identity` comes from the SEAT and never from the peer's own `hello` — the seat was minted against a
 * Sanctum credential, so the plane knows whose it is without asking (`App\Support\ArenaIdentity`, T4).
 */
export type RosterRow = {
  peer: string;
  role: 'host' | 'guest';
  status: 'invited' | 'accepted' | 'connected' | 'left';
  identity: { callsign: string; team: { tag: string; name: string } | null } | null;
};
/**
 * `name` is WHAT THE HOST CALLED THE MATCH, and `null` is a real answer — a room can exist with no match
 * in it at all (the connectivity probe of T1 has none), so the plane does not demand one. It is the room's
 * own fact, which is why the panel that draws it reads it from HERE and treats the address it arrived on
 * as nothing better than a fallback for a room that never went through a plane (`shell/roomLobby.ts`).
 */
export type Room = { code: string; status: string; name: string | null; map: string | null; max_seats: number; expires_at: string };
export type RoomGrant = {
  room: Room;
  seat: Seat;
  iceServers: RTCIceServer[];
  turnOffered: boolean;
  broker: { url: string; key: string };
};

/**
 * WHERE AN ICE LIST COMES FROM — the grant's once, the endpoint every time after.
 *
 * The grant already carries a list minted for this session, so asking again at handshake time would buy
 * the same answer with a round trip. Every LATER ask is a restart (`Peer.restart`), and a restart needs a
 * credential that has not aged out — which is the whole finding of `ops/turn.md §5-bis`.
 *
 * One definition because two callers need it: the probe and a hosted room.
 */
export function iceProvider(api: Api, bearer: () => string, granted: RTCIceServer[]): () => Promise<RTCIceServer[]> {
  let first: RTCIceServer[] | null = granted;
  return async () => {
    const held = first;
    first = null;
    return held ?? api.ice(bearer());
  };
}

/**
 * Where the plane answers, when the page is not told otherwise.
 *
 * ONE definition for every page that talks to it: the probe and the game are served from several places
 * and a constant per page would be a second description of a topology the ingress already holds. `?api=`
 * overrides it, which is what makes both usable against a LAN address.
 */
export const DEFAULT_API_BASE = 'https://api.a-airsoft.app';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

export class Api {
  constructor(readonly base: string) {}

  private async call(path: string, init: RequestInit & { bearer?: string; roomToken?: string }): Promise<unknown> {
    const headers: Record<string, string> = { Accept: 'application/json', ...(init.headers as Record<string, string>) };
    if (init.body) headers['Content-Type'] = 'application/json';
    if (init.bearer) headers.Authorization = `Bearer ${init.bearer}`;
    if (init.roomToken) headers['X-Arena-Room-Token'] = init.roomToken;

    const res = await fetch(`${this.base}${path}`, { ...init, headers });
    const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
    if (!res.ok) {
      // The status is not collapsed into one message (`code-quality.md §12.2`): full, expired and closed
      // send a player to three different next moves, and the backend already tells them apart.
      throw new ApiError(res.status, body?.error ?? `http_${res.status}`, body?.message ?? body?.error ?? `HTTP ${res.status}`);
    }
    return body;
  }

  /*
   * THERE IS NO `login()` HERE, and its absence is the decision.
   *
   * It existed, it POSTed to `/v1/auth/login`, and its only caller was the email+password form on
   * `/link.html` — the second sign-in screen `bearer.ts` argues must not exist. The QR replaced that form
   * (`link/door.ts`), so the method was dead, and dead code that implements exactly what was just removed
   * is the shortest path back to having two doors: the next person needing a bearer in a hurry finds a
   * ready-made one. The way in is the phone.
   */

  /**
   * A FRESH `iceServers`, mid-session — what an ICE restart has to ask for (`ops/turn.md §5-bis`).
   *
   * Measured in production: an expired TURN credential keeps relaying an allocation that already exists
   * and refuses a new one. A restart asks for a new one, so reusing the list from the handshake leaves
   * the pair that just failed with no relay to fall into.
   */
  async ice(bearer: string): Promise<RTCIceServer[]> {
    const out = (await this.call('/v1/arena/ice', { bearer })) as { iceServers: RTCIceServer[] };
    return out.iceServers;
  }

  openRoom(bearer: string, map: string | null, maxSeats?: number): Promise<RoomGrant> {
    return this.call('/v1/arena/rooms', { method: 'POST', bearer, body: JSON.stringify({ map, ...(maxSeats ? { max_seats: maxSeats } : {}) }) }) as Promise<RoomGrant>;
  }

  joinRoom(bearer: string, code: string): Promise<RoomGrant> {
    return this.call(`/v1/arena/rooms/${code}/join`, { method: 'POST', bearer }) as Promise<RoomGrant>;
  }

  /**
   * THE ROOM AND WHO IS IN IT — read with the SEAT's token and not with the person's bearer.
   *
   * A room's roster is not an account's business: what asks for it is an Arena page holding a seat, on
   * another origin, with no session of its own (`ResolveArenaRoomSeat`). Same credential as every other
   * verb a seat performs, and the reason the header is its own.
   */
  room(code: string, roomToken: string): Promise<{ room: Room; me: { peer: string; role: string }; roster: RosterRow[] }> {
    return this.call(`/v1/arena/rooms/${code}`, { roomToken }) as Promise<{ room: Room; me: { peer: string; role: string }; roster: RosterRow[] }>;
  }

  channelAuth(code: string, roomToken: string, socketId: string): Promise<{ auth: string; channel: string }> {
    return this.call(`/v1/arena/rooms/${code}/channel-auth`, {
      method: 'POST',
      roomToken,
      body: JSON.stringify({ socket_id: socketId }),
    }) as Promise<{ auth: string; channel: string }>;
  }

  signal(code: string, roomToken: string, to: string | null, kind: string, payload: unknown): Promise<unknown> {
    return this.call(`/v1/arena/rooms/${code}/signal`, {
      method: 'POST',
      roomToken,
      body: JSON.stringify({ to, kind, payload }),
    });
  }

  connected(code: string, roomToken: string): Promise<unknown> {
    return this.call(`/v1/arena/rooms/${code}/connected`, { method: 'POST', roomToken });
  }

  /**
   * The measurement. `turn_offered` is NOT sent: what was on offer is a fact about the backend's own
   * configuration, and asking the measured party to describe its conditions is how a population gets
   * mislabelled. The server stamps it.
   *
   * `kind` DOES come from here, because it is the one thing the server cannot infer: the same endpoint
   * carries the game's link and every voice pair, and they are two populations. Absent means `game`.
   */
  iceReport(code: string, roomToken: string, r: { kind?: 'game' | 'voice'; local_type: string; remote_type: string; local_protocol: string | null; connect_ms: number | null }): Promise<{ relayed: boolean; turn_offered: boolean }> {
    return this.call(`/v1/arena/rooms/${code}/ice-report`, { method: 'POST', roomToken, body: JSON.stringify(r) }) as Promise<{ relayed: boolean; turn_offered: boolean }>;
  }

  leave(code: string, roomToken: string): Promise<unknown> {
    return this.call(`/v1/arena/rooms/${code}/leave`, { method: 'POST', roomToken });
  }
}
